import { useState } from 'react';
import { cn } from '@/lib/utils/cn';
import { Input } from '@/components/ui';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

export function MultiSelect({
  question,
  value,
  onChange,
  disabled,
  errors,
  displayOrder,
}: QuestionComponentProps<string[] | null>) {
  const [otherText, setOtherText] = useState('');
  const options = question.config?.options || [];
  const layout = question.config?.layout || 'vertical';
  const hasOtherOption = question.config?.hasOtherOption;
  const otherLabel = question.config?.otherOptionLabel || 'Other';
  const exclusiveOptions = question.config?.exclusiveOptions || [];
  const maxSelections = question.config?.maxSelections;
  const minSelections = question.config?.minSelections;

  const selectedValues = value || [];

  // Apply display order if provided
  const orderedOptions = displayOrder
    ? displayOrder.map((i) => options[i]).filter(Boolean)
    : options;

  const handleToggle = (optionValue: string, isExclusive: boolean) => {
    if (disabled) return;

    let newValues: string[];

    if (selectedValues.includes(optionValue)) {
      // Deselect
      newValues = selectedValues.filter((v) => v !== optionValue);
    } else {
      // Select
      if (isExclusive || exclusiveOptions.includes(optionValue)) {
        // Exclusive option - clear all others
        newValues = [optionValue];
      } else {
        // Check if any exclusive option is selected
        const hasExclusive = selectedValues.some(
          (v) =>
            exclusiveOptions.includes(v) ||
            options.find((o) => o.value === v)?.isExclusive
        );

        if (hasExclusive) {
          // Clear exclusive and add new
          newValues = [optionValue];
        } else if (maxSelections && selectedValues.length >= maxSelections) {
          // Max reached
          return;
        } else {
          newValues = [...selectedValues, optionValue];
        }
      }
    }

    onChange(newValues.length > 0 ? newValues : null);
  };

  const handleOtherChange = (text: string) => {
    setOtherText(text);
    const otherValue = `__other__:${text}`;
    const withoutOther = selectedValues.filter((v) => !v.startsWith('__other__:'));
    onChange([...withoutOther, otherValue]);
  };

  const isOtherSelected = selectedValues.some((v) => v.startsWith('__other__:'));
  const atMaxSelections = maxSelections && selectedValues.length >= maxSelections;

  return (
    <QuestionWrapper question={question} errors={errors}>
      {/* Selection limit hint */}
      {(minSelections || maxSelections) && (
        <p className="mb-3 text-sm text-gray-500">
          {minSelections && maxSelections
            ? `Select ${minSelections} to ${maxSelections} options`
            : minSelections
            ? `Select at least ${minSelections} options`
            : `Select up to ${maxSelections} options`}
        </p>
      )}

      <div
        className={cn(
          'space-y-3',
          layout === 'horizontal' && 'flex flex-wrap gap-4 space-y-0',
          layout === 'grid' && `grid gap-3 grid-cols-${question.config?.gridColumns || 2}`
        )}
      >
        {orderedOptions.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const isExclusive = option.isExclusive || exclusiveOptions.includes(option.value);
          const isDisabledByMax = atMaxSelections && !isSelected;

          return (
            <label
              key={option.id}
              className={cn(
                'option-item',
                isSelected && 'option-item-selected',
                (disabled || isDisabledByMax) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="checkbox"
                value={option.value}
                checked={isSelected}
                onChange={() => handleToggle(option.value, isExclusive)}
                disabled={disabled || !!isDisabledByMax}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-900">{option.text}</span>
            </label>
          );
        })}

        {/* Other option */}
        {hasOtherOption && (
          <div>
            <label
              className={cn(
                'option-item',
                isOtherSelected && 'option-item-selected',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="checkbox"
                checked={isOtherSelected}
                onChange={() => {
                  if (isOtherSelected) {
                    onChange(selectedValues.filter((v) => !v.startsWith('__other__:')));
                  } else {
                    handleOtherChange('');
                  }
                }}
                disabled={disabled}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-900">{otherLabel}</span>
            </label>
            {isOtherSelected && (
              <Input
                type="text"
                value={otherText}
                onChange={(e) => handleOtherChange(e.target.value)}
                placeholder="Please specify..."
                disabled={disabled}
                className="mt-2 ml-8"
              />
            )}
          </div>
        )}
      </div>
    </QuestionWrapper>
  );
}
